import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  X,
  User,
  Mail,
  Phone,
  MapPin,
  Building2,
  FileText,
} from "lucide-react";
import toast from "react-hot-toast";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { COUNTRY_CODES, US_STATES } from "./constants";
import AutocompleteInput from "./shared/AutocompleteInput";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";

const STATUS_OPTIONS = ["Active", "Passive", "DNC", "Archive"];

const DEPARTMENT_OPTIONS = [
  "Accounting",
  "Accounts Payable",
  "Accounts Receivable",
  "Administration",
  "Customer Service",
  "Customer Support",
  "Finance",
  "Human Resources",
  "Information Technology",
  "Marketing",
  "Operations",
  "Payroll",
  "Sales",
  "Training",
  "Travel",
];

// Zod schema for form validation
const contactFormSchema = z.object({
  firstName: z.string().min(1, "First name is required").max(100),
  middleName: z.string().max(100).optional().or(z.literal("")),
  lastName: z.string().min(1, "Last name is required").max(100),
  nickname: z.string().max(100).optional().or(z.literal("")),
  status: z.enum(["Active", "Passive", "DNC", "Archive"], {
    required_error: "Status is required",
  }),
  owner: z.string().min(1, "Owner is required"),
  ownerDisplay: z.string().optional().or(z.literal("")), // Display name for owner
  primaryEmail: z
    .string()
    .min(1, "Primary email is required")
    .email("Please enter a valid email address"),
  secondaryEmail: z
    .union([
      z.string().email("Please enter a valid email address"),
      z.literal(""),
    ])
    .optional(),
  clientDirectPhone: z.object({
    countryCode: z.string().default("+1"),
    number: z
      .string()
      .regex(/^\d*$/, "Phone number must contain only digits")
      .optional()
      .or(z.literal("")),
  }),
  mobilePhone: z.object({
    countryCode: z.string().default("+1"),
    number: z
      .string()
      .regex(/^\d*$/, "Phone number must contain only digits")
      .optional()
      .or(z.literal("")),
  }),
  otherPhone: z.object({
    countryCode: z.string().default("+1"),
    number: z
      .string()
      .regex(/^\d*$/, "Phone number must contain only digits")
      .optional()
      .or(z.literal("")),
  }),
  title: z.string().optional().or(z.literal("")),
  company_id: z.string().optional().or(z.literal("")),
  companyDisplay: z.string().optional().or(z.literal("")), // Display name for company
  department: z.string().optional().or(z.literal("")),
  reportsTo_id: z.string().optional().or(z.literal("")),
  reportsToDisplay: z.string().optional().or(z.literal("")), // Display name for reportsTo
  source: z.string().max(100).optional().or(z.literal("")),
  generalContactComments: z.string().optional().or(z.literal("")),
  address: z.object({
    address1: z.string().optional().or(z.literal("")),
    address2: z.string().optional().or(z.literal("")),
    city: z.string().optional().or(z.literal("")),
    state: z.union([z.enum(US_STATES), z.literal("")]).optional(),
    zip: z.string().optional().or(z.literal("")),
    country: z.string().optional().or(z.literal("")),
    countryDisplay: z.string().optional().or(z.literal("")), // Display name for country
  }),
});

// Initial form values
const defaultValues = {
  firstName: "",
  middleName: "",
  lastName: "",
  nickname: "",
  status: "",
  owner: "",
  ownerDisplay: "",
  primaryEmail: "",
  secondaryEmail: "",
  clientDirectPhone: {
    countryCode: "+1",
    number: "",
  },
  mobilePhone: {
    countryCode: "+1",
    number: "",
  },
  otherPhone: {
    countryCode: "+1",
    number: "",
  },
  title: "",
  company_id: "",
  companyDisplay: "",
  department: "",
  reportsTo_id: "",
  reportsToDisplay: "",
  source: "",
  generalContactComments: "",
  address: {
    address1: "",
    address2: "",
    city: "",
    state: "",
    zip: "",
    country: "",
    countryDisplay: "",
  },
};

const ContactFormModal = ({ isOpen, onClose, onSuccess }) => {
  const form = useForm({
    resolver: zodResolver(contactFormSchema),
    defaultValues,
    mode: "onChange",
  });

  const {
    handleSubmit,
    reset,
    setValue,
    control,
    formState: { isSubmitting, isValid },
  } = form;

  useEffect(() => {
    if (isOpen) {
      reset(defaultValues);
    } else {
      // Reset form when modal is closed
      reset(defaultValues);
    }
  }, [isOpen, reset]);

  const onSubmit = async (data) => {
    try {
      // Prepare phone numbers and assign empty values based on type instead of null
      // Remove display fields (they're only for UI)
      const {
        companyDisplay,
        ownerDisplay,
        reportsToDisplay,
        ...formDataWithoutDisplay
      } = data;
      const submitData = {
        ...formDataWithoutDisplay,
        // Phone numbers: assign empty object if no number, otherwise keep the object
        clientDirectPhone: data.clientDirectPhone.number
          ? {
              countryCode: data.clientDirectPhone.countryCode,
              number: data.clientDirectPhone.number,
            }
          : {
              countryCode: data.clientDirectPhone.countryCode || "+1",
              number: "",
            },
        mobilePhone: data.mobilePhone.number
          ? {
              countryCode: data.mobilePhone.countryCode,
              number: data.mobilePhone.number,
            }
          : {
              countryCode: data.mobilePhone.countryCode || "+1",
              number: "",
            },
        otherPhone: data.otherPhone.number
          ? {
              countryCode: data.otherPhone.countryCode,
              number: data.otherPhone.number,
            }
          : {
              countryCode: data.otherPhone.countryCode || "+1",
              number: "",
            },
        // Address: assign empty object with all fields as empty strings
        // Remove countryDisplay (it's only for UI)
        address: {
          address1: data.address.address1 || "",
          address2: data.address.address2 || "",
          city: data.address.city || "",
          state: data.address.state || "",
          zip: data.address.zip || "",
          country: data.address.country || "",
        },
        // String fields: assign empty string instead of null
        middleName: data.middleName || "",
        nickname: data.nickname || "",
        secondaryEmail: data.secondaryEmail || "",
        title: data.title || "",
        department: data.department || "",
        reportsTo_id: data.reportsTo_id || "",
        source: data.source || "",
        generalContactComments: data.generalContactComments || "",
        created_by: "Test User",
      };

      const response = await fetch(`${BACKEND_URL}/api/contacts`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(submitData),
      });

      if (response.ok) {
        const result = await response.json();
        toast.success("Contact created successfully");
        onSuccess && onSuccess(result);
        reset(defaultValues);
        onClose();
      } else {
        const errorData = await response.json().catch(() => ({}));

        // Handle different error response formats
        let errorMessage = "Failed to create contact";

        if (typeof errorData.detail === "string") {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail)) {
          // FastAPI validation errors format
          errorMessage = errorData.detail
            .map((err) => `${err.loc?.join(" -> ") || "Field"}: ${err.msg}`)
            .join(", ");
        } else if (errorData.detail && typeof errorData.detail === "object") {
          // Object with error details
          errorMessage = JSON.stringify(errorData.detail);
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }

        toast.error(errorMessage);
        // Don't close modal or reset form on error
      }
    } catch (error) {
      console.error("Error creating contact:", error);
      toast.error("Error creating contact. Please try again.");
      // Don't close modal or reset form on error
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full my-8">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <User className="h-5 w-5" />
            Add Contact
          </h2>
          <Button
            onClick={onClose}
            variant="ghost"
            size="icon"
            className="text-white hover:text-gray-200 hover:bg-transparent"
          >
            <X className="h-6 w-6" />
          </Button>
        </div>

        {/* Form */}
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="overflow-y-auto"
            style={{ maxHeight: "calc(90vh - 140px)" }}
          >
            <div className="p-6 space-y-6">
              {/* Basic Information Section */}
              <div className="border-b pb-6">
                <div className="flex items-center gap-2 mb-4">
                  <User className="h-5 w-5 text-purple-600" />
                  <h3 className="text-lg font-semibold text-gray-900">
                    Basic Information
                  </h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="firstName"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <Label>
                          First Name <span className="text-red-500">*</span>
                        </Label>
                        <FormControl>
                          <Input
                            {...field}
                            maxLength={100}
                            placeholder="Enter first name"
                            className={
                              fieldState.error
                                ? "border-red-500 focus-visible:ring-red-500"
                                : ""
                            }
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="middleName"
                    render={({ field }) => (
                      <FormItem>
                        <Label>Middle Name</Label>
                        <FormControl>
                          <Input
                            {...field}
                            maxLength={100}
                            placeholder="Enter middle name"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="lastName"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <Label>
                          Last Name <span className="text-red-500">*</span>
                        </Label>
                        <FormControl>
                          <Input
                            {...field}
                            maxLength={100}
                            placeholder="Enter last name"
                            className={
                              fieldState.error
                                ? "border-red-500 focus-visible:ring-red-500"
                                : ""
                            }
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="nickname"
                    render={({ field }) => (
                      <FormItem>
                        <Label>Nickname</Label>
                        <FormControl>
                          <Input
                            {...field}
                            maxLength={100}
                            placeholder="Enter nickname"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="status"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <Label>
                          Status <span className="text-red-500">*</span>
                        </Label>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value || undefined}
                        >
                          <FormControl>
                            <SelectTrigger
                              className={
                                fieldState.error
                                  ? "border-red-500 focus:ring-red-500"
                                  : ""
                              }
                            >
                              <SelectValue placeholder="Select status" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent
                            side="bottom"
                            align="start"
                            sideOffset={4}
                          >
                            {STATUS_OPTIONS.map((status) => (
                              <SelectItem key={status} value={status}>
                                {status}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem>
                        <Label>Title</Label>
                        <FormControl>
                          <Input {...field} placeholder="Enter job title" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              {/* Company & Organization Section */}
              <div className="border-b pb-6">
                <div className="flex items-center gap-2 mb-4">
                  <Building2 className="h-5 w-5 text-purple-600" />
                  <h3 className="text-lg font-semibold text-gray-900">
                    Company & Organization
                  </h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Company Autocomplete */}
                  <FormField
                    control={form.control}
                    name="company_id"
                    render={({ field, fieldState }) => (
                      <AutocompleteInput
                        control={control}
                        fieldName="company_id"
                        displayFieldName="companyDisplay"
                        label="Company"
                        placeholder="Search company..."
                        searchEndpoint="/api/companies/search"
                        valueKey="id"
                        displayKey="name"
                        field={field}
                        fieldState={fieldState}
                        setValue={setValue}
                      />
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="department"
                    render={({ field }) => (
                      <FormItem>
                        <Label>Department</Label>
                        <Select
                          onValueChange={(value) => field.onChange(value || "")}
                          defaultValue={field.value}
                          value={field.value || undefined}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select department" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent
                            side="bottom"
                            align="start"
                            sideOffset={4}
                          >
                            {DEPARTMENT_OPTIONS.map((dept) => (
                              <SelectItem key={dept} value={dept}>
                                {dept}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Owner Autocomplete */}
                  <FormField
                    control={form.control}
                    name="owner"
                    render={({ field, fieldState }) => (
                      <AutocompleteInput
                        control={control}
                        fieldName="owner"
                        displayFieldName="ownerDisplay"
                        label="Owner"
                        required={true}
                        placeholder="Search owner..."
                        searchEndpoint="/api/users/search"
                        valueKey="firstName"
                        displayFormatter={(user) => {
                          // Combine firstName and lastName for display
                          const firstName = user.firstName || "";
                          const lastName = user.lastName || "";
                          return (
                            `${firstName} ${lastName}`.trim() ||
                            user.email ||
                            ""
                          );
                        }}
                        field={field}
                        fieldState={fieldState}
                        setValue={setValue}
                      />
                    )}
                  />

                  {/* Reports To Autocomplete */}
                  <FormField
                    control={form.control}
                    name="reportsTo_id"
                    render={({ field, fieldState }) => (
                      <AutocompleteInput
                        control={control}
                        fieldName="reportsTo_id"
                        displayFieldName="reportsToDisplay"
                        label="Reports To"
                        placeholder="Search contact..."
                        searchEndpoint="/api/contacts/search"
                        valueKey="id"
                        displayFormatter={(contact) => {
                          // Combine firstName and lastName for display
                          const firstName = contact.firstName || "";
                          const lastName = contact.lastName || "";
                          return (
                            `${firstName} ${lastName}`.trim() ||
                            contact.primaryEmail ||
                            ""
                          );
                        }}
                        field={field}
                        fieldState={fieldState}
                        setValue={setValue}
                      />
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="source"
                    render={({ field }) => (
                      <FormItem className="md:col-span-2">
                        <Label>Source</Label>
                        <FormControl>
                          <Input
                            {...field}
                            maxLength={100}
                            placeholder="Enter source"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              {/* Contact Details Section */}
              <div className="border-b pb-6">
                <div className="flex items-center gap-2 mb-4">
                  <Mail className="h-5 w-5 text-purple-600" />
                  <h3 className="text-lg font-semibold text-gray-900">
                    Contact Details
                  </h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="primaryEmail"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <Label>
                          Primary Email <span className="text-red-500">*</span>
                        </Label>
                        <FormControl>
                          <Input
                            {...field}
                            type="email"
                            placeholder="primary@email.com"
                            className={
                              fieldState.error
                                ? "border-red-500 focus-visible:ring-red-500"
                                : ""
                            }
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="secondaryEmail"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <Label>Secondary Email</Label>
                        <FormControl>
                          <Input
                            {...field}
                            type="email"
                            placeholder="secondary@email.com"
                            className={
                              fieldState.error
                                ? "border-red-500 focus-visible:ring-red-500"
                                : ""
                            }
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              {/* Phone Numbers Section */}
              <div className="border-b pb-6">
                <div className="flex items-center gap-2 mb-4">
                  <Phone className="h-5 w-5 text-purple-600" />
                  <h3 className="text-lg font-semibold text-gray-900">
                    Phone Numbers
                  </h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Client Direct Phone */}
                  <div className="space-y-2">
                    <Label>Client Direct Phone</Label>
                    <div className="grid grid-cols-12 gap-2">
                      <div className="col-span-4">
                        <FormField
                          control={form.control}
                          name="clientDirectPhone.countryCode"
                          render={({ field, fieldState }) => (
                            <FormItem>
                              <Select
                                onValueChange={field.onChange}
                                value={field.value || "+1"}
                              >
                                <FormControl>
                                  <SelectTrigger
                                    className={
                                      fieldState.error
                                        ? "border-red-500 focus:ring-red-500"
                                        : ""
                                    }
                                  >
                                    <SelectValue />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent
                                  side="bottom"
                                  align="start"
                                  sideOffset={4}
                                >
                                  {COUNTRY_CODES.map(({ code, country }) => (
                                    <SelectItem key={code} value={code}>
                                      {code} ({country})
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      <div className="col-span-8">
                        <FormField
                          control={form.control}
                          name="clientDirectPhone.number"
                          render={({ field, fieldState }) => (
                            <FormItem>
                              <FormControl>
                                <Input
                                  {...field}
                                  type="tel"
                                  maxLength={10}
                                  inputMode="numeric"
                                  placeholder="Phone number"
                                  onChange={(e) => {
                                    const value = e.target.value.replace(
                                      /\D/g,
                                      ""
                                    );
                                    field.onChange(value);
                                  }}
                                  className={
                                    fieldState.error
                                      ? "border-red-500 focus-visible:ring-red-500"
                                      : ""
                                  }
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Mobile Phone */}
                  <div className="space-y-2">
                    <Label>Mobile Phone</Label>
                    <div className="grid grid-cols-12 gap-2">
                      <div className="col-span-4">
                        <FormField
                          control={form.control}
                          name="mobilePhone.countryCode"
                          render={({ field, fieldState }) => (
                            <FormItem>
                              <Select
                                onValueChange={field.onChange}
                                value={field.value || "+1"}
                              >
                                <FormControl>
                                  <SelectTrigger
                                    className={
                                      fieldState.error
                                        ? "border-red-500 focus:ring-red-500"
                                        : ""
                                    }
                                  >
                                    <SelectValue />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent
                                  side="bottom"
                                  align="start"
                                  sideOffset={4}
                                >
                                  {COUNTRY_CODES.map(({ code, country }) => (
                                    <SelectItem key={code} value={code}>
                                      {code} ({country})
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      <div className="col-span-8">
                        <FormField
                          control={form.control}
                          name="mobilePhone.number"
                          render={({ field, fieldState }) => (
                            <FormItem>
                              <FormControl>
                                <Input
                                  {...field}
                                  type="tel"
                                  maxLength={10}
                                  inputMode="numeric"
                                  placeholder="Phone number"
                                  onChange={(e) => {
                                    const value = e.target.value.replace(
                                      /\D/g,
                                      ""
                                    );
                                    field.onChange(value);
                                  }}
                                  className={
                                    fieldState.error
                                      ? "border-red-500 focus-visible:ring-red-500"
                                      : ""
                                  }
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Other Phone */}
                  <div className="space-y-2">
                    <Label>Other Phone</Label>
                    <div className="grid grid-cols-12 gap-2">
                      <div className="col-span-4">
                        <FormField
                          control={form.control}
                          name="otherPhone.countryCode"
                          render={({ field, fieldState }) => (
                            <FormItem>
                              <Select
                                onValueChange={field.onChange}
                                value={field.value || "+1"}
                              >
                                <FormControl>
                                  <SelectTrigger
                                    className={
                                      fieldState.error
                                        ? "border-red-500 focus:ring-red-500"
                                        : ""
                                    }
                                  >
                                    <SelectValue />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent
                                  side="bottom"
                                  align="start"
                                  sideOffset={4}
                                >
                                  {COUNTRY_CODES.map(({ code, country }) => (
                                    <SelectItem key={code} value={code}>
                                      {code} ({country})
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      <div className="col-span-8">
                        <FormField
                          control={form.control}
                          name="otherPhone.number"
                          render={({ field, fieldState }) => (
                            <FormItem>
                              <FormControl>
                                <Input
                                  {...field}
                                  type="tel"
                                  maxLength={10}
                                  inputMode="numeric"
                                  placeholder="Phone number"
                                  onChange={(e) => {
                                    const value = e.target.value.replace(
                                      /\D/g,
                                      ""
                                    );
                                    field.onChange(value);
                                  }}
                                  className={
                                    fieldState.error
                                      ? "border-red-500 focus-visible:ring-red-500"
                                      : ""
                                  }
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Address Section */}
              <div className="border-b pb-6">
                <div className="flex items-center gap-2 mb-4">
                  <MapPin className="h-5 w-5 text-purple-600" />
                  <h3 className="text-lg font-semibold text-gray-900">
                    Address
                  </h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="address.address1"
                    render={({ field }) => (
                      <FormItem className="md:col-span-2">
                        <Label>Address Line 1</Label>
                        <FormControl>
                          <Input
                            {...field}
                            placeholder="Enter street address"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="address.address2"
                    render={({ field }) => (
                      <FormItem className="md:col-span-2">
                        <Label>Address Line 2</Label>
                        <FormControl>
                          <Input
                            {...field}
                            placeholder="Apartment, suite, etc. (optional)"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="address.city"
                    render={({ field }) => (
                      <FormItem>
                        <Label>City</Label>
                        <FormControl>
                          <Input {...field} placeholder="Enter city" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="address.state"
                    render={({ field }) => (
                      <FormItem>
                        <Label>State</Label>
                        <Select
                          onValueChange={(value) => field.onChange(value || "")}
                          value={field.value || undefined}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select state" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent
                            side="bottom"
                            align="start"
                            sideOffset={4}
                          >
                            {US_STATES.map((state) => (
                              <SelectItem key={state} value={state}>
                                {state}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="address.zip"
                    render={({ field }) => (
                      <FormItem>
                        <Label>ZIP Code</Label>
                        <FormControl>
                          <Input {...field} placeholder="Enter zip code" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="address.country"
                    render={({ field, fieldState }) => (
                      <AutocompleteInput
                        control={control}
                        fieldName="address.country"
                        displayFieldName="address.countryDisplay"
                        label="Country"
                        placeholder="Search country..."
                        searchEndpoint="/api/company/country-codes"
                        valueKey="id"
                        displayKey="country"
                        queryParamName="search"
                        responseTransformer={(data) => {
                          // Extract unique countries from country_codes response
                          const countrySet = new Set();
                          const countryList = [];

                          (data.country_codes || []).forEach((item) => {
                            if (item.country && !countrySet.has(item.country)) {
                              countrySet.add(item.country);
                              countryList.push({
                                id: item.country, // Use country name as id
                                country: item.country, // Display value
                              });
                            }
                          });

                          return countryList.sort((a, b) =>
                            a.country.localeCompare(b.country)
                          );
                        }}
                        field={field}
                        fieldState={fieldState}
                        setValue={setValue}
                      />
                    )}
                  />
                </div>
              </div>

              {/* Additional Information Section */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <FileText className="h-5 w-5 text-purple-600" />
                  <h3 className="text-lg font-semibold text-gray-900">
                    Additional Information
                  </h3>
                </div>

                <FormField
                  control={form.control}
                  name="generalContactComments"
                  render={({ field }) => (
                    <FormItem>
                      <Label>General Contact Comments</Label>
                      <FormControl>
                        <Textarea
                          {...field}
                          rows={4}
                          placeholder="Enter any additional comments or notes"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t">
              <Button
                type="button"
                onClick={onClose}
                variant="outline"
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting || !isValid}
                className={
                  !isSubmitting && isValid
                    ? "bg-purple-600 hover:bg-purple-700"
                    : "bg-gray-400 cursor-not-allowed"
                }
              >
                {isSubmitting ? "Creating..." : "Create Contact"}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
};

export default ContactFormModal;
